/**
 * Server-token ladder for the server-hosted app (ARCHITECTURE §5.4, the
 * offline contract). The media server accepts two bearer kinds: the
 * broker's server-scoped token (12 h) and its own server-local session
 * (12 h) minted by `POST /v1/auth/local-token` from an expired-but-authentic
 * server-scoped token — signature and key id are checked, expiry is not,
 * so the LAST server-scoped token the broker issued is a permanent exchange
 * input. Both live in localStorage under the `rago.local.` prefix and are
 * never deleted on expiry: with the cloud down, the ladder keeps swapping
 * the stale cloud token for fresh local sessions every 12 h for any role
 * with a live grant on the server (no offline clock — the Plex way).
 */
import type { MintedServerToken } from "@rago/web-shared";
import { api } from "./apiClient";
import { ApiProblemError, auth } from "./authManager";
import { LOCAL_PREFIX } from "./localAuthStore";

export const LOCAL_SERVER_ID_KEY = `${LOCAL_PREFIX}serverId`;
const cloudKey = (serverId: string) => `${LOCAL_PREFIX}serverToken.${serverId}`;
const localKey = (serverId: string) => `${LOCAL_PREFIX}session.${serverId}`;

/** Fresh-enough margin: the connection manager re-mints 60 s early too. */
const MARGIN_MS = 60_000;

export interface StoredToken {
  token: string;
  /** ms since epoch */
  expiresAt: number;
}

/** No token of either kind can be produced (never signed in here, or the
 * cloud is down and nothing is stored). */
export class NoServerSessionError extends Error {
  constructor() {
    super("No session for this server");
    this.name = "NoServerSessionError";
  }
}

/** The server refused the local exchange, or the broker has no grant: the
 * user's access to this server was revoked. `definitive` — the broker
 * itself has no live grant for the account. The server's refusal of the
 * offline exchange is not: it also refuses a token it cannot verify. */
export class AccessRevokedError extends Error {
  constructor(public readonly definitive: boolean) {
    super("Access to this server was revoked");
    this.name = "AccessRevokedError";
  }
}

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function read(key: string): StoredToken | null {
  const raw = storage()?.getItem(key);
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Partial<StoredToken>;
    return typeof t.token === "string" && typeof t.expiresAt === "number" ? (t as StoredToken) : null;
  } catch {
    return null;
  }
}

function write(key: string, t: StoredToken): void {
  try {
    storage()?.setItem(key, JSON.stringify(t));
  } catch {
    // memory only
  }
}

function fresh(t: StoredToken | null, now: number): t is StoredToken {
  return t !== null && t.expiresAt - now > MARGIN_MS;
}

/** The stored server-scoped token, fresh or stale — for the exchange. */
export function storedCloudToken(serverId: string): StoredToken | null {
  return read(cloudKey(serverId));
}

/** Where the exchange request goes: the page's own origin (the server). */
function serverOrigin(): string {
  return typeof window === "undefined" ? "" : window.location.origin;
}

/** The broker's answer for an account with no live grant on the server. */
export function noGrant(err: unknown): boolean {
  return err instanceof ApiProblemError && (err.problem.status === 403 || err.problem.status === 404);
}

/** A server-scoped token from the broker, not stored. Bearer via apiClient:
 * the stored session token, or `bearer` — a session not adopted yet, whose
 * check must not hang on a slow cloud. Throws what api() throws. */
export async function requestCloudToken(serverId: string, bearer?: string): Promise<StoredToken> {
  const minted = await api<MintedServerToken>(`/v1/servers/${serverId}/token`, {
    method: "POST",
    ...(bearer !== undefined ? { token: bearer, signal: AbortSignal.timeout(8_000) } : {}),
  });
  return { token: minted.token, expiresAt: Date.parse(minted.expiresAt) };
}

export function rememberCloudToken(serverId: string, t: StoredToken): void {
  write(cloudKey(serverId), t);
}

async function mintCloud(serverId: string): Promise<StoredToken | null> {
  // Network failure and 5xx fall through to the local rung; a 403/404 is a
  // missing grant.
  try {
    const t = await requestCloudToken(serverId);
    rememberCloudToken(serverId, t);
    return t;
  } catch (err) {
    if (noGrant(err)) throw new AccessRevokedError(true);
    return null;
  }
}

async function exchangeLocal(serverId: string, cloud: StoredToken): Promise<StoredToken | null> {
  let res: Response;
  try {
    res = await fetch(`${serverOrigin()}/v1/auth/local-token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token: cloud.token }),
    });
  } catch {
    return null;
  }
  if (res.status === 403) throw new AccessRevokedError(false);
  if (!res.ok) return null;
  const body = (await res.json().catch(() => null)) as { token?: string; expiresAt?: string } | null;
  if (!body?.token || !body.expiresAt) return null;
  const t = { token: body.token, expiresAt: Date.parse(body.expiresAt) };
  write(localKey(serverId), t);
  return t;
}

/**
 * A bearer the media server will accept, from the cheapest rung up:
 * 1. the stored server-scoped token while fresh;
 * 2. the stored server-local session while fresh;
 * 3. a new server-scoped token from the broker (signed in, cloud up);
 * 4. a server-local session exchanged from the stored (possibly expired)
 *    server-scoped token — the offline rung.
 * `force` skips 1–2: the server answered 401 with what we had, so only a
 * different token helps.
 */
export async function getServerToken(
  serverId: string,
  opts: { force?: boolean; now?: number } = {},
): Promise<StoredToken> {
  const now = opts.now ?? Date.now();
  const cloud = read(cloudKey(serverId));
  if (!opts.force) {
    if (fresh(cloud, now)) return cloud;
    const local = read(localKey(serverId));
    if (fresh(local, now)) return local;
  }
  if (auth.sessionToken()) {
    const minted = await mintCloud(serverId);
    if (minted) return minted;
  }
  if (cloud) {
    const local = await exchangeLocal(serverId, cloud);
    if (local) return local;
  }
  throw new NoServerSessionError();
}

export function rememberLocalServerId(serverId: string): void {
  try {
    storage()?.setItem(LOCAL_SERVER_ID_KEY, serverId);
  } catch {
    // memory only
  }
}

export function localServerId(): string | null {
  return storage()?.getItem(LOCAL_SERVER_ID_KEY) ?? null;
}
